import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';

import { HelpSection, Tour } from './guided-tour.model';
import { GuidedTourService } from './guided-tour.service';

/**
 * Botón **Ayuda** de un módulo: abre un diálogo con los tours guiados disponibles y
 * las secciones de referencia. Es la generalización del patrón de
 * `users/reports/user-documentation`, para que cualquier módulo lo reutilice pasando
 * sus tours y sus textos.
 */
@Component({
    selector: 'app-module-help',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        DialogModule,
        DividerModule,
        MenuModule,
        TooltipModule,
    ],
    templateUrl: './module-help.component.html',
    styleUrls: ['./module-help.component.scss'],
})
export class ModuleHelpComponent implements OnInit {
    /** Nombre del módulo, para el encabezado del diálogo. */
    @Input() moduleName = '';

    @Input() tours: Tour[] = [];

    @Input() sections: HelpSection[] = [];

    /** Etiqueta del botón; en barras apretadas se puede dejar vacía. */
    @Input() label = 'Ayuda';

    visible = false;

    /** `'tours'` o el id de una sección. */
    selectedId = 'tours';

    menuItems: MenuItem[] = [];

    constructor(private tourService: GuidedTourService) {}

    ngOnInit(): void {
        this.buildMenu();
    }

    /** Los tours que la persona puede seguir con sus permisos. */
    get availableTours(): Tour[] {
        return this.tours.filter((tour) => (tour.when ? tour.when() : true));
    }

    get selectedSection(): HelpSection | undefined {
        return this.sections.find((section) => section.id === this.selectedId);
    }

    open(): void {
        this.buildMenu();
        this.visible = true;
    }

    close(): void {
        this.visible = false;
    }

    select(id: string): void {
        this.selectedId = id;
    }

    /** El tour se sigue en la pantalla real, así que el diálogo se cierra. */
    startTour(tour: Tour): void {
        this.visible = false;
        this.tourService.start(tour);
    }

    private buildMenu(): void {
        const items: MenuItem[] = [
            {
                label: 'Tutoriales',
                items: [
                    {
                        label: 'Tours guiados',
                        icon: 'pi pi-compass',
                        command: () => this.select('tours'),
                    },
                ],
            },
        ];

        if (this.sections.length) {
            items.push({
                label: 'Referencia',
                items: this.sections.map((section) => ({
                    label: section.title,
                    icon: section.icon,
                    command: () => this.select(section.id),
                })),
            });
        }

        this.menuItems = items;
    }
}
